// src/pages/task/views/TimelineView.tsx
// Gantt-like timeline. Bars span start_date → due_date and can be dragged to
// shift both dates or resized from the right edge to change the due date.
//
// A task with neither date has no bar: it is listed in the undated section
// instead of being invented from created_at.

import { useCallback, useMemo, useRef, useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Button } from '@react/components';

import type { Task, TaskStatusDefinition } from '@core/types/task';

import { formatDate, getEntityId, getTaskDueDate } from '@/features/task/logic';

import {
  addDays,
  buildStatusLegend,
  daysBetween,
  formatShortDate,
  getTaskStartDate,
  moveIsoToDay,
  parseDate,
  resolveStatusStyle,
  shiftIsoByDays,
  startOfDay,
  type StatusStyle,
} from './planning';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** New dates produced by a drag; only the supplied fields are written. */
export interface TimelineDateChange {
  start_date?: string;
  due_date?: string;
}

interface TimelineViewProps {
  tasks: Task[];
  statusDefinitions?: TaskStatusDefinition[];
  onTaskClick: (id: string) => void;
  /** Commit a bar move (both dates) or a resize (due date). */
  onTaskDatesChange?: (taskId: string, dates: TimelineDateChange) => void | Promise<void>;
}

type ZoomLevel = 'day' | 'week' | 'month';
type DragMode = 'move' | 'resize';

const ZOOM_DAYS: Record<ZoomLevel, number> = { day: 14, week: 42, month: 90 };
const LABEL_WIDTH = 220;

interface TimelineBar {
  id: string;
  title: string;
  style: StatusStyle;
  /** Original wire values, so a shift keeps the time of day. */
  startIso?: string;
  dueIso?: string;
  start: Date;
  end: Date;
  /** Percent of the track. */
  left: number;
  width: number;
  durationDays: number;
  hasStart: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const TimelineView = observer(({
  tasks,
  statusDefinitions,
  onTaskClick,
  onTaskDatesChange,
}: TimelineViewProps) => {
  const { t, locale } = useI18n();
  const today = useMemo(() => startOfDay(new Date()), []);

  const [zoom, setZoom] = useState<ZoomLevel>('week');
  const [offset, setOffset] = useState(0);

  const totalDays = ZOOM_DAYS[zoom];
  const rangeStart = useMemo(() => addDays(today, offset - 7), [today, offset]);

  const dateMarkers = useMemo(() => {
    const step = zoom === 'day' ? 1 : zoom === 'week' ? 7 : 14;
    const markers: { key: string; label: string; position: number }[] = [];
    for (let i = 0; i <= totalDays; i += step) {
      const date = addDays(rangeStart, i);
      markers.push({
        key: `${i}`,
        label: formatShortDate(date, locale),
        position: (i / totalDays) * 100,
      });
    }
    return markers;
  }, [rangeStart, totalDays, zoom, locale]);

  const { bars, undated } = useMemo(() => {
    const dated: TimelineBar[] = [];
    const withoutDates: Task[] = [];

    for (const task of tasks) {
      const startIso = getTaskStartDate(task);
      const dueIso = getTaskDueDate(task);
      const start = parseDate(startIso);
      const due = parseDate(dueIso);

      if (!start && !due) {
        withoutDates.push(task);
        continue;
      }

      const barStart = start ?? due!;
      const barEnd = due && due.getTime() >= barStart.getTime() ? due : addDays(barStart, 1);
      const durationDays = Math.max(1, daysBetween(barStart, barEnd));
      const offsetDays = daysBetween(rangeStart, barStart);

      const rawLeft = (offsetDays / totalDays) * 100;
      const rawWidth = (durationDays / totalDays) * 100;
      if (rawLeft + rawWidth <= 0 || rawLeft >= 100) continue;

      const left = Math.max(0, rawLeft);
      dated.push({
        id: getEntityId(task),
        title: (task as unknown as { title?: string }).title || t('common.untitled'),
        style: resolveStatusStyle(task, statusDefinitions),
        startIso,
        dueIso,
        start: barStart,
        end: barEnd,
        left,
        width: Math.max(0.6, Math.min(rawWidth + Math.min(0, rawLeft), 100 - left)),
        durationDays,
        hasStart: !!start,
      });
    }

    return { bars: dated, undated: withoutDates };
  }, [tasks, statusDefinitions, rangeStart, totalDays, t]);

  const todayPos = useMemo(() => {
    const pos = (daysBetween(rangeStart, today) / totalDays) * 100;
    return pos >= 0 && pos <= 100 ? pos : null;
  }, [rangeStart, today, totalDays]);

  const legend = useMemo(() => buildStatusLegend(statusDefinitions), [statusDefinitions]);

  /**
   * Translate a drag into concrete dates. Moving shifts both ends; resizing
   * only writes the due date, and gives a start-only task the due date it was
   * missing.
   */
  const commitDrag = useCallback(
    (bar: TimelineBar, mode: DragMode, days: number) => {
      if (!onTaskDatesChange || days === 0) return;

      if (mode === 'move') {
        const change: TimelineDateChange = {};
        if (bar.startIso) change.start_date = shiftIsoByDays(bar.startIso, days);
        if (bar.dueIso) change.due_date = shiftIsoByDays(bar.dueIso, days);
        if (!bar.startIso && !bar.dueIso) return;
        void onTaskDatesChange(bar.id, change);
        return;
      }

      const nextEnd = addDays(bar.end, days);
      void onTaskDatesChange(bar.id, { due_date: moveIsoToDay(bar.dueIso, nextEnd) });
    },
    [onTaskDatesChange],
  );

  const scrollBy = useCallback(
    (direction: -1 | 1) => setOffset((value) => value + direction * Math.floor(totalDays / 3)),
    [totalDays],
  );

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1">
          {(['day', 'week', 'month'] as ZoomLevel[]).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setZoom(value)}
              aria-pressed={zoom === value}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                zoom === value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {t(`workspace.timeline.${value}`)}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => scrollBy(-1)} aria-label={t('common.previous')}>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setOffset(0)}>
            {t('workspace.calendar.today')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => scrollBy(1)} aria-label={t('common.next')}>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {legend.map((entry) => (
            <div key={entry.id} className="flex items-center gap-1">
              <span
                className={`inline-block h-2.5 w-2.5 rounded-sm ${entry.className}`}
                style={entry.hex ? { backgroundColor: entry.hex } : undefined}
              />
              <span className="text-[10px] text-muted-foreground">
                {entry.label || t(entry.labelKey)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {onTaskDatesChange && (
        <p className="text-[11px] text-muted-foreground">{t('task.planning.timelineHint')}</p>
      )}

      {/* Chart */}
      <div className="rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-[800px]">
            {/* Date header */}
            <div className="relative h-10 border-b border-border bg-muted/30">
              <div
                className="absolute left-0 flex h-full items-center border-r border-border bg-muted/50 px-3"
                style={{ width: LABEL_WIDTH }}
              >
                <span className="text-xs font-semibold text-muted-foreground">{t('task.title')}</span>
              </div>
              {dateMarkers.map((marker) => (
                <div
                  key={marker.key}
                  className="absolute top-0 flex h-full items-center"
                  style={{ left: `calc(${LABEL_WIDTH}px + (100% - ${LABEL_WIDTH}px) * ${marker.position / 100})` }}
                >
                  <span className="whitespace-nowrap pl-1 text-[10px] text-muted-foreground">
                    {marker.label}
                  </span>
                </div>
              ))}
              {todayPos !== null && (
                <div
                  className="absolute top-0 z-10 h-full w-px bg-destructive"
                  style={{ left: `calc(${LABEL_WIDTH}px + (100% - ${LABEL_WIDTH}px) * ${todayPos / 100})` }}
                />
              )}
            </div>

            {/* Rows */}
            <div className="divide-y divide-border">
              {bars.length === 0 && undated.length === 0 && (
                <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
                  {t('task.noItems')}
                </div>
              )}

              {bars.map((bar) => (
                <TimelineRow
                  key={bar.id}
                  bar={bar}
                  totalDays={totalDays}
                  todayPos={todayPos}
                  editable={!!onTaskDatesChange}
                  onTaskClick={onTaskClick}
                  onCommit={commitDrag}
                  locale={locale}
                />
              ))}
            </div>

            {/* Undated */}
            {undated.length > 0 && (
              <>
                <div className="border-t-2 border-border bg-muted/30 px-3 py-2">
                  <span className="text-xs font-semibold text-muted-foreground">
                    {t('workspace.timeline.noDates')} ({undated.length})
                  </span>
                </div>
                <div className="divide-y divide-border">
                  {undated.map((task) => {
                    const id = getEntityId(task);
                    return (
                      <div key={id} className="flex h-10 items-center transition-colors hover:bg-muted/20">
                        <div
                          className="flex-shrink-0 truncate border-r border-border px-3"
                          style={{ width: LABEL_WIDTH }}
                        >
                          <button
                            type="button"
                            onClick={() => onTaskClick(id)}
                            className="truncate text-left text-xs font-medium text-primary hover:underline"
                          >
                            {(task as unknown as { title?: string }).title || t('common.untitled')}
                          </button>
                        </div>
                        <div className="flex-1 px-3">
                          <span className="text-[10px] italic text-muted-foreground">
                            {t('workspace.timeline.noDateAssigned')}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

// ---------------------------------------------------------------------------
// Row with a draggable / resizable bar
// ---------------------------------------------------------------------------

interface TimelineRowProps {
  bar: TimelineBar;
  totalDays: number;
  todayPos: number | null;
  editable: boolean;
  onTaskClick: (id: string) => void;
  onCommit: (bar: TimelineBar, mode: DragMode, days: number) => void;
  locale: string;
}

function TimelineRow({ bar, totalDays, todayPos, editable, onTaskClick, onCommit, locale }: TimelineRowProps) {
  const { t } = useI18n();
  const trackRef = useRef<HTMLDivElement>(null);
  // The ref is authoritative for the committed value; the state only drives
  // the live preview.
  const dragRef = useRef<
    { mode: DragMode; startX: number; pxPerDay: number; moved: boolean; days: number } | null
  >(null);
  const [drag, setDrag] = useState<{ mode: DragMode; days: number } | null>(null);

  /** Pointer dragging (not dnd-kit) because a Gantt needs day snapping on a
   *  continuous axis rather than discrete drop targets. */
  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>, mode: DragMode) => {
      if (!editable || event.button !== 0) return;
      const track = trackRef.current;
      if (!track) return;
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      dragRef.current = {
        mode,
        startX: event.clientX,
        pxPerDay: track.getBoundingClientRect().width / totalDays,
        moved: false,
        days: 0,
      };
      setDrag({ mode, days: 0 });
    },
    [editable, totalDays],
  );

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLElement>) => {
    const state = dragRef.current;
    if (!state || state.pxPerDay <= 0) return;
    const dx = event.clientX - state.startX;
    if (Math.abs(dx) > 3) state.moved = true;
    let days = Math.round(dx / state.pxPerDay);
    if (state.mode === 'resize') days = Math.max(days, 1 - bar.durationDays);
    state.days = days;
    setDrag({ mode: state.mode, days });
  }, [bar.durationDays]);

  const endDrag = useCallback(
    (event: React.PointerEvent<HTMLElement>, commit: boolean) => {
      const state = dragRef.current;
      dragRef.current = null;
      if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      setDrag(null);
      if (!state) return;
      if (commit && state.moved && state.days !== 0) onCommit(bar, state.mode, state.days);
      else if (commit && !state.moved && state.mode === 'move') onTaskClick(bar.id);
    },
    [bar, onCommit, onTaskClick],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onTaskClick(bar.id);
        return;
      }
      if (!editable || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
      if (!event.shiftKey && !event.altKey) return;
      event.preventDefault();
      const step = event.key === 'ArrowLeft' ? -1 : 1;
      if (event.altKey) {
        if (bar.durationDays + step < 1) return;
        onCommit(bar, 'resize', step);
      } else {
        onCommit(bar, 'move', step);
      }
    },
    [bar, editable, onCommit, onTaskClick],
  );

  const dayWidth = 100 / totalDays;
  const previewLeft = bar.left + (drag?.mode === 'move' ? drag.days * dayWidth : 0);
  const previewWidth = bar.width + (drag?.mode === 'resize' ? (drag?.days ?? 0) * dayWidth : 0);

  const previewStart = drag?.mode === 'move' ? addDays(bar.start, drag.days) : bar.start;
  const previewEnd =
    drag?.mode === 'move'
      ? addDays(bar.end, drag.days)
      : drag?.mode === 'resize'
        ? addDays(bar.end, drag.days)
        : bar.end;

  const rangeLabel = `${formatDate(previewStart.toISOString(), locale)} – ${formatDate(previewEnd.toISOString(), locale)}`;

  return (
    <div className="flex h-10 items-center transition-colors hover:bg-muted/20">
      <div className="flex-shrink-0 truncate border-r border-border px-3" style={{ width: LABEL_WIDTH }}>
        <button
          type="button"
          onClick={() => onTaskClick(bar.id)}
          className="truncate text-left text-xs font-medium text-primary hover:underline"
        >
          {bar.title}
        </button>
      </div>

      <div ref={trackRef} className="relative h-full flex-1">
        <div
          role="button"
          tabIndex={0}
          aria-label={`${bar.title} — ${rangeLabel}`}
          title={`${bar.title} (${rangeLabel})${editable ? ` · ${t('task.planning.timelineHint')}` : ''}`}
          onPointerDown={(event) => handlePointerDown(event, 'move')}
          onPointerMove={handlePointerMove}
          onPointerUp={(event) => endDrag(event, true)}
          onPointerCancel={(event) => endDrag(event, false)}
          onKeyDown={handleKeyDown}
          onClick={(event) => {
            // The pointer handlers own click-vs-drag; keep a plain click from
            // firing twice when the pointer sequence already opened the task.
            event.preventDefault();
          }}
          style={{
            left: `${Math.max(0, previewLeft)}%`,
            width: `${Math.max(0.6, previewWidth)}%`,
            ...(bar.style.hex ? { backgroundColor: bar.style.hex } : {}),
          }}
          className={`absolute top-1.5 flex h-7 items-center rounded px-2 opacity-80 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
            bar.style.className
          } ${editable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${drag ? 'opacity-100 ring-2 ring-primary/60' : ''}`}
        >
          <span className="truncate text-[10px] font-medium text-white">
            {previewWidth > 8 ? bar.title : ''}
          </span>

          {editable && (
            <span
              aria-hidden="true"
              onPointerDown={(event) => handlePointerDown(event, 'resize')}
              onPointerMove={handlePointerMove}
              onPointerUp={(event) => endDrag(event, true)}
              onPointerCancel={(event) => endDrag(event, false)}
              className="absolute right-0 top-0 h-full w-2 cursor-ew-resize rounded-r bg-black/10 hover:bg-black/25"
            />
          )}
        </div>

        {todayPos !== null && (
          <div
            className="pointer-events-none absolute top-0 h-full w-px bg-destructive/40"
            style={{ left: `${todayPos}%` }}
          />
        )}

        {drag && drag.days !== 0 && (
          <span
            className="pointer-events-none absolute top-0 z-10 rounded bg-foreground px-1 text-[10px] text-background"
            style={{ left: `${Math.min(92, Math.max(0, previewLeft))}%` }}
          >
            {drag.days > 0 ? `+${drag.days}` : drag.days}
          </span>
        )}
      </div>
    </div>
  );
}
